import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import AppContainer from '../Components/AppContainer';
import { Box, Text, VStack, Modal, HStack, Button, useTheme, Center, ScrollView, Skeleton, FormControl, Input, Spinner } from 'native-base';
import APIAction from '../../Actions/APIAction';
import Trans from '../Components/Trans';
import { v4 as uuidv4 } from 'uuid';
import { createIconSetFromFontello } from 'react-native-vector-icons';
import lineAwesomeConfig from '../../Assets/Fontello/line-awesome-config.json';
import main from '../../Assets/Styles/main.json';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ClockOpenState from '../../Recoil/ClockOpenState';
import { useRecoilState } from 'recoil';
import ShiftList from './Components/ShiftList';
import TranslationAction from '../../Actions/TranslationAction';
import UserAction from '../../Actions/UserAction';
import GeneralAction from '../../Actions/GeneralAction';
import LoadingScreen from '../General/LoadingScreen';

const mainStyle = StyleSheet.create(main);
const Icon = createIconSetFromFontello(lineAwesomeConfig);

const ClockScreen = (props) => {

    const
        [waitCode, setWaitCode] = useState(false),
        { colors } = useTheme(),
        firstLoad = useRef(true),
        [shifts, setShifts] = useState(null),
        apiEmployeeShifts = useRef([]),
        [enteredCode, setEnteredCode] = useState('****'),
        [openModal, setOpenModal] = useState(false),
        [ready, setReady] = useState(false),
        [clockOpen, setClockOpen] = useRecoilState(ClockOpenState),
        loopInterval = useRef(null),
        [translations, setTranslations] = useState({
            'Search for employees': ''
        }),
        [searchTerm, setSearchTerm] = useState(''),
        searchWait = useRef(null),
        [client, setClient] = useState(null)
        ;


    const getClient = useCallback(async () => {
        let user = await UserAction.getUser();
        let apiClient = await APIAction.request({
            method: 'GET',
            url: `${user.client}`,
            cache: false,
        });
        return apiClient;
    }, []);


    const onFirstLoad = useCallback((load = true) => {
        const init = async () => {
            let employeeShifts = [];
            if (load) {

                //get client
                let apiClient = await getClient();
                setClient(apiClient);

                //get employee shifts
                let newEmployeeShifts = await APIAction.request({
                    method: 'GET',
                    url: '/api/get/clock/employee_shifts',
                });
                employeeShifts = newEmployeeShifts['hydra:member'];
                apiEmployeeShifts.current = employeeShifts;
            } else {
                employeeShifts = apiEmployeeShifts.current;
            }

            //filter employee shifts
            if (searchTerm && searchTerm.length > 3) {
                employeeShifts = employeeShifts.filter((shift) => {
                    let name = shift?.employee?.user?.firstName + ' ' + shift?.employee?.user?.lastName;

                    let prepName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
                    let prepSearch = searchTerm.toLowerCase().replace(/[^a-z0-9]/g, '');

                    return prepName.includes(prepSearch);
                });
            }

            let sortedEmployeeShifts = [];
            //sort by department
            for (let shift of employeeShifts) {
                //let department = shift?.shift?.department?.id;
                //don't sort on id, sort on name
                let department = shift?.shift?.department?.name;

                if (department) {
                    department = department.toLowerCase().replace(/[^a-z0-9]/g, '');

                    department += `:${shift.shift.department.name}`;
                }
                if (!department) department = 'NONE';

                //get department key if exists
                let departmentKey = false;
                for (let key in sortedEmployeeShifts) {
                    if (key.split(/:(.*)/s)[0] === department.split(/:(.*)/s)[0]) {
                        departmentKey = key;
                    }
                }

                //if department not in sortedEmployeeShifts
                if (!departmentKey) {
                    sortedEmployeeShifts[department] = [];
                    departmentKey = department;
                }

                sortedEmployeeShifts[departmentKey].push(shift);
            }

            let newShifts = [];

            for (let [department, employeeShifts] of Object.entries(sortedEmployeeShifts)) {
                if (department !== 'NONE') {
                    let departmentName = department.split(/:(.*)/s)[1];

                    newShifts.push(<ShiftList key={uuidv4()} name={departmentName} employeeShifts={employeeShifts} callback={onFirstLoad} />);
                } else {
                    newShifts.push(<ShiftList key={uuidv4()} name={<Trans>Other</Trans>} employeeShifts={employeeShifts} callback={onFirstLoad} />);
                }
            }

            setShifts(newShifts);
            setReady(true);
        };
        init();
    }, [setShifts, searchTerm, getClient, setClient]);

    useEffect(() => {
        clearInterval(loopInterval.current);
        loopInterval.current = setInterval(() => {
            if (!clockOpen) {
                onFirstLoad();
            }
        }, 300000);
    }, [onFirstLoad, clockOpen]);

    const onChangeCode = async (value) => {
        let oldCode = enteredCode.replace(/\*/g, '');
        let newCode = '';
        if (value === '-1') {
            newCode = oldCode.slice(0, -1);
        } else {
            newCode = oldCode + value;
        }

        let paddedCode = newCode.padEnd(4, '*');
        setEnteredCode(paddedCode);

        if (newCode.length === 4) {
            setWaitCode(true);

            //get code
            let codeClient = await getClient();

            setWaitCode(false);

            if (newCode === codeClient.clockCode || codeClient.clockLocked === false) {
                setOpenModal(false);
                AsyncStorage.removeItem('ClockLocked').then(() => {
                    clearInterval(loopInterval.current);
                    props.navigation.navigate('App');
                });
            } else {
                await GeneralAction.toast('error', <Trans>Invalid code</Trans>);
            }
            newCode = '';

            setEnteredCode('****');
        }
    };

    const closeModal = () => {
        setEnteredCode('****');
        setOpenModal(false);
    };

    useEffect(() => {
        const translate = async () => {
            let newTranslations = await TranslationAction.translateInLine(Object.keys(translations));
            setTranslations(newTranslations);
        };

        if (firstLoad.current) {
            firstLoad.current = false;
            onFirstLoad();
            translate();
        }
    }, [onFirstLoad, translations]);

    useEffect(() => {
        clearTimeout(searchWait.current);
        searchWait.current = null;
        searchWait.current = setTimeout(() => {
            onFirstLoad(false);
        }, 1000);

    }, [searchTerm, onFirstLoad]);

    return (
        <>
            {client ?
                <>
                    <AppContainer
                        noTabBar
                        noScroll
                    >
                        <ScrollView style={{ height: '97vh' }}>
                            {ready ?
                                <VStack space={2}>
                                    <Box>
                                        <FormControl>
                                            <Input placeholder={translations['Search for employees']} onChangeText={(text) => {
                                                setSearchTerm(text);
                                            }} />
                                        </FormControl>
                                    </Box>
                                    <Box>
                                        {shifts}
                                    </Box>
                                </VStack>
                                :
                                <>
                                    <Skeleton h={85} marginBottom={15} />
                                    <Skeleton h={85} marginBottom={15} />
                                    <Skeleton h={85} marginBottom={15} />
                                </>
                            }
                        </ScrollView>
                        <Box height={'3vh'} marginBottom={6} >
                            <Button variant={'subtle'} colorScheme={'danger'}
                                onPress={() => {
                                    if (client.clockLocked) {
                                        setOpenModal(true);
                                    } else {
                                        AsyncStorage.removeItem('ClockLocked').then(() => {
                                            clearInterval(loopInterval.current);
                                            props.navigation.navigate('App');
                                        });
                                    }
                                }}
                            >
                                <Icon color={colors['danger']['600']} size={16} name={'times'} />
                            </Button>
                            {/*<ImagePicker open={true} directToCam={true} frontCamera={true} />*/}
                        </Box>
                    </AppContainer>
                    <Modal
                        isOpen={openModal}
                        onClose={() => closeModal()}
                    >
                        <Modal.Content>
                            <Modal.CloseButton />
                            <Modal.Header><Text style={[mainStyle.modalTitle]}><Trans>Code</Trans></Text></Modal.Header>
                            <Modal.Body>
                                <Center>
                                    <HStack space={2}>
                                        <Text fontSize={'xl'}>{enteredCode}</Text>
                                        {waitCode &&
                                            <Spinner size={'sm'} />
                                        }
                                    </HStack>
                                </Center>
                                <VStack
                                    space={2}
                                >
                                    <HStack
                                        space={2}
                                    >
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('1')}
                                        >1</Button>
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('2')}
                                        >2</Button>
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('3')}
                                        >3</Button>
                                    </HStack>
                                    <HStack
                                        space={2}
                                    >
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('4')}
                                        >4</Button>
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('5')}
                                        >5</Button>
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('6')}
                                        >6</Button>
                                    </HStack>
                                    <HStack
                                        space={2}
                                    >
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('7')}
                                        >7</Button>
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('8')}
                                        >8</Button>
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('9')}
                                        >9</Button>
                                    </HStack>
                                    <HStack
                                        space={2}
                                    >
                                        <Button
                                            colorScheme={'danger'}
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => closeModal()}
                                        ><Text color={'#fff'}><Trans>Cancel</Trans></Text></Button>
                                        <Button
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('0')}
                                        >0</Button>
                                        <Button
                                            colorScheme={'warning'}
                                            flex={1}
                                            flexGrow={1}
                                            flexShrink={1}
                                            disabled={waitCode}
                                            onPress={() => onChangeCode('-1')}
                                        ><Icon color={'#fff'} size={16} name={'arrow-left'} /></Button>
                                    </HStack>
                                </VStack>
                            </Modal.Body>
                        </Modal.Content>
                    </Modal>
                </>
                :
                <LoadingScreen />
            }
        </>
    );
};

export default ClockScreen;